import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Settings, Shield, UserCog, Flag } from 'lucide-react'
import { CommunityLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/community/CommunityLayout'
import { useCommunity } from '@/components/community/CommunityContext'
import { SectionLoading, StatusPill, Avatar } from '@/components/community/ui'
import {
  listReports, resolveReport, updateMyMemberProfile,
  type ReportResponse, type MemberProfileRequest,
} from '@/services/communityApi'

export default function CommunitySettingsPage() {
  const { t } = useTranslation()
  const { community, canManage } = useCommunity()
  const [tab, setTab] = useState<'profile' | 'moderation'>('profile')

  if (!community) return null

  return (
    <CommunityLayout>
      <GovPageTitle title={t('community.settings.title')} subtitle={t('community.settings.subtitle')} icon={Settings} />

      <div className="flex gap-1 border-b border-gray-200 mb-4" role="tablist">
        <TabButton active={tab === 'profile'} onClick={() => setTab('profile')}>{t('community.settings.tabProfile')}</TabButton>
        {canManage && <TabButton active={tab === 'moderation'} onClick={() => setTab('moderation')}>{t('community.settings.tabModeration')}</TabButton>}
      </div>

      {tab === 'profile' ? <MyProfileSection /> : canManage ? <ModerationSection communityId={community.id} /> : null}
    </CommunityLayout>
  )
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button role="tab" aria-selected={active} onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px min-h-[40px] ${active ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
      {children}
    </button>
  )
}

function MyProfileSection() {
  const { t } = useTranslation()
  const [form, setForm] = useState<MemberProfileRequest>({ skills: [], interests: [] })
  const [saving, setSaving] = useState(false)
  const [done, setDone] = useState(false)

  const field = (k: 'skills' | 'interests', v: string) => setForm((p) => ({ ...p, [k]: v.split(',').map((s) => s.trim()).filter(Boolean) }))

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true); setDone(false)
    try { await updateMyMemberProfile(form); setDone(true) }
    catch { /* ignore */ } finally { setSaving(false) }
  }

  const inputCls = 'w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none'

  return (
    <div className="space-y-4">
      <GovCard>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('community.settings.skills')}</label>
            <input value={(form.skills ?? []).join(', ')} onChange={(e) => field('skills', e.target.value)} className={inputCls} placeholder={t('community.settings.skillsHint')} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('community.settings.interests')}</label>
            <input value={(form.interests ?? []).join(', ')} onChange={(e) => field('interests', e.target.value)} className={inputCls} placeholder={t('community.settings.interestsHint')} />
          </div>
          <div className="flex items-center gap-3">
            <GovButton variant="primary" size="sm" type="submit" disabled={saving}>{saving ? t('common.saving') : t('community.actions.saveChanges')}</GovButton>
            {done && <span className="text-sm text-emerald-600">{t('community.settings.saved')}</span>}
          </div>
        </form>
      </GovCard>
    </div>
  )
}

function ModerationSection({ communityId }: { communityId: number }) {
  const { t } = useTranslation()
  const [reports, setReports] = useState<ReportResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<number | null>(null)

  const load = useCallback(() => {
    setLoading(true)
    listReports(communityId, 'PENDING', 0, 50)
      .then((p) => setReports(p.content))
      .catch(() => setReports([]))
      .finally(() => setLoading(false))
  }, [communityId])

  useEffect(() => { load() }, [load])

  const resolve = async (r: ReportResponse, status: string) => {
    setBusyId(r.id)
    try { await resolveReport(r.id, { status, resolutionNote: status === 'RESOLVED' ? t('community.settings.resolvedByLeader') : t('community.settings.dismissedByLeader') }); setReports((p) => p.filter((x) => x.id !== r.id)) } catch { /* ignore */ }
    finally { setBusyId(null) }
  }

  return (
    <div className="space-y-4">
      <GovCard>
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0"><Shield className="w-4 h-4 text-blue-600" aria-hidden="true" /></div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900">{t('community.settings.moderationHelp')}</p>
            <Link to="/community-dashboard/members" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1 mt-1 min-h-[40px]"><UserCog className="w-3.5 h-3.5" aria-hidden="true" />{t('community.settings.manageMembers')}</Link>
          </div>
        </div>
      </GovCard>

      <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Flag className="w-4 h-4" aria-hidden="true" />{t('community.settings.reportQueue')}</h2>
      {loading ? <SectionLoading rows={3} /> : reports.length === 0 ? (
        <GovEmptyState icon="✅" title={t('community.settings.noReports')} />
      ) : (
        <div className="space-y-3">
          {reports.map((r) => (
            <GovCard key={r.id}>
              <div className="flex items-start gap-3">
                <Avatar name={r.reporter.fullName} image={r.reporter.profileImage} size={36} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-900">{t(`community.settings.reason.${r.reason}`, r.reason)}</span>
                    <StatusPill status={r.status} />
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{t('community.settings.reportedBy')} {r.reporter.fullName}</p>
                  {r.details && <p className="text-sm text-gray-600 mt-1">{r.details}</p>}
                  <div className="flex items-center gap-4 mt-2">
                    <button onClick={() => resolve(r, 'RESOLVED')} disabled={busyId === r.id}
                      className="text-sm font-medium text-emerald-600 hover:underline disabled:opacity-50 min-h-[40px]">{t('community.settings.resolve')}</button>
                    <button onClick={() => resolve(r, 'DISMISSED')} disabled={busyId === r.id}
                      className="text-sm font-medium text-gray-600 hover:underline disabled:opacity-50 min-h-[40px]">{t('community.settings.dismiss')}</button>
                  </div>
                </div>
              </div>
            </GovCard>
          ))}
        </div>
      )}
    </div>
  )
}